import React, { useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { LogoMark } from "@/components/app/Logo";
import { ArrowLeft, Loader2, Plus, Users as UsersIcon, Trash2, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const fetcher = (url) => axios.get(url).then(r => r.data);

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <Button size="sm" variant="outline" onClick={copy} className="h-7 px-2">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

const ROLE_LABELS = {
  superadmin: { label: "Superadmin", tone: "bg-primary/15 text-primary border-primary/40" },
  admin: { label: "Admin", tone: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40" },
  viewer: { label: "Viewer", tone: "bg-slate-500/10 text-slate-700 border-slate-500/40" },
};

export default function Users() {
  const { user } = useAuth();
  const { data: users, mutate, isLoading } = useSWR(user?.role === "superadmin" ? "/users" : null, fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [pwDialog, setPwDialog] = useState(null); // { user, newPassword }

  if (user && user.role !== "superadmin") {
    return <Navigate to="/vendors" replace />;
  }

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  const submit = async () => {
    if (!form.email.trim() || !form.password.trim()) { toast.error("Email and password required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post("/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
      });
      toast.success(`User ${data.email} created`);
      setOpen(false);
      setPwDialog({ user: data, newPassword: form.password });
      setForm({ email: "", password: "", name: "", role: "admin" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    try {
      await axios.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const resetPassword = async (u) => {
    const newPw = genPassword();
    try {
      await axios.patch(`/users/${u.id}/password`, { password: newPw });
      setPwDialog({ user: u, newPassword: newPw });
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-3 z-40 mx-auto max-w-5xl px-3 sm:px-4 lg:px-6">
        <div className="glass rounded-full px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0">
              <ArrowLeft className="h-3.5 w-3.5" /> Projects
            </Link>
            <div className="h-4 w-px bg-border/60 mx-1" />
            <div className="flex items-center gap-1.5">
              <UsersIcon className="h-3.5 w-3.5 text-primary" />
              <span className="font-heading text-sm font-bold">Team & access</span>
            </div>
          </div>
          <div className="hidden md:block text-[11px] text-muted-foreground font-mono uppercase tracking-wider">
            Create teammates and control who can log in
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-heading text-lg font-bold">Members ({users?.length || 0})</h2>
            <p className="text-sm text-muted-foreground">
              <b>Superadmin</b> can manage users. <b>Admin</b> can edit BOQs and scenarios. <b>Viewer</b> is read-only.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-user"><Plus className="h-4 w-4 mr-1" /> New user</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite a new user</DialogTitle>
                <DialogDescription>They'll sign in with the email and password you set here.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="u-email">Email</Label>
                  <Input id="u-email" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="teammate@nathabitat.com" autoFocus data-testid="input-user-email" />
                </div>
                <div>
                  <Label htmlFor="u-name">Name (optional)</Label>
                  <Input id="u-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Jane Doe" data-testid="input-user-name" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm(f => ({...f, role: v}))}>
                    <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin — full edit access</SelectItem>
                      <SelectItem value="viewer">Viewer — read-only</SelectItem>
                      <SelectItem value="superadmin">Superadmin — full + user mgmt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="u-pw">Password</Label>
                  <div className="flex gap-2">
                    <Input id="u-pw" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="min 6 characters" data-testid="input-user-password" />
                    <Button type="button" variant="outline" onClick={() => setForm(f => ({...f, password: genPassword()}))}>Generate</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Share this password with them securely — they can change it later.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="btn-submit-user">{saving ? "Creating…" : "Create user"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading || !users ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Email</th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Role</th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Added</th>
                  <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const r = ROLE_LABELS[u.role] || ROLE_LABELS.admin;
                  const isSelf = u.id === user?.id;
                  return (
                    <tr key={u.id} className="border-b border-border last:border-none hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium" data-testid={`user-email-${u.id}`}>
                        {u.email}
                        {isSelf && <Badge variant="outline" className="ml-2 text-[9px]">YOU</Badge>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.name || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-[10px] ${r.tone}`}>{r.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => resetPassword(u)} data-testid={`btn-reset-pw-${u.id}`}>
                            <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                          </Button>
                          {!isSelf && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" data-testid={`btn-delete-user-${u.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
                                  <AlertDialogDescription>They will lose access immediately. This cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => remove(u)}>Remove user</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={!!pwDialog} onOpenChange={(o) => !o && setPwDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share these credentials</DialogTitle>
            <DialogDescription>Copy them now — the password is only shown once.</DialogDescription>
          </DialogHeader>
          {pwDialog && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Email</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-sm">{pwDialog.user.email}</div>
                  <CopyBtn value={pwDialog.user.email} />
                </div>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Password</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-sm" data-testid="new-user-password">{pwDialog.newPassword}</div>
                  <CopyBtn value={pwDialog.newPassword} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Sign-in URL: <code>{window.location.origin}/login</code></p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPwDialog(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
